import { useEffect, useRef, useState } from 'react';
import type * as Blockly from 'blockly/core';
import { requireWorkspace } from '../lib/workspace';
import { runAssistantTurn, requireApiKey, GeminiError } from '../lib/gemini';
import { useStore, type AiMessage, type AiToolCall } from '../state/store';
import { IconBot, IconSend } from './icons';

const EXAMPLE_PROMPTS = [
  'Blink the onboard LED on GPIO 2 every 500 ms',
  'Read a button on GPIO 4 with a pull-up and print the state to serial',
  'Connect to Wi-Fi "MyNetwork" and print the local IP',
];

export function AiPanel() {
  const open = useStore((s) => s.aiOpen);
  const model = useStore((s) => s.settings.model);
  const apiKey = useStore((s) => s.settings.apiKey);
  const setSettingsOpen = useStore((s) => s.setSettingsOpen);
  const pushLine = useStore((s) => s.pushLine);

  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [messages, busy, open]);

  async function send(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setInput('');
    setBusy(true);
    const toolChips: AiToolCall[] = [];

    setMessages((m) => [...m, { role: 'user', text: trimmed }]);

    try {
      const apiKey = requireApiKey();
      const result = await runAssistantTurn({
        apiKey,
        model,
        workspace: requireWorkspace() as unknown as Blockly.Workspace,
        history: messages,
        userText: trimmed,
        onToolCall: (call) => {
          toolChips.push(call);
          setMessages((m) => {
            const last = m[m.length - 1];
            if (last && last.role === 'assistant' && last.toolCalls && last.text === '') {
              return [...m.slice(0, -1), { ...last, toolCalls: [...last.toolCalls, call] }];
            }
            return [...m, { role: 'assistant', text: '', toolCalls: [call] }];
          });
        },
      });
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: result.text, toolCalls: toolChips.length ? toolChips : undefined },
      ]);
    } catch (err) {
      const message =
        err instanceof GeminiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'The assistant request failed.';
      setMessages((m) => [...m, { role: 'assistant', text: message, isError: true }]);
      pushLine('err', `AI assistant: ${message}`);
    } finally {
      setBusy(false);
    }
  }

  const needsKey = apiKey.trim().length === 0;

  return (
    <aside className="ai-area" aria-label="AI assistant panel" aria-hidden={!open}>
      <div className="ai-panel">
        <div className="ai-head">
          <IconBot />
          <span className="ai-head-title">AI Assistant</span>
          <span className="ai-head-model">{model}</span>
        </div>

        <div className="ai-log" ref={logRef} aria-live="polite">
          {messages.length === 0 && (
            <div className="ai-empty">
              <IconBot width={28} height={28} />
              <p>
                Ask for a program and the assistant builds the blocks on this canvas, fixes
                wiring problems, and explains the generated code.
              </p>
              {needsKey ? (
                <button className="btn" style={{ marginTop: 10 }} onClick={() => setSettingsOpen(true)}>
                  Add your Google AI Studio key
                </button>
              ) : (
                EXAMPLE_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    className="btn"
                    style={{ marginTop: 8, width: '100%', justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={() => void send(prompt)}
                    disabled={busy}
                  >
                    {prompt}
                  </button>
                ))
              )}
            </div>
          )}

          {messages.map((message, index) => (
            <div key={index} style={{ display: 'contents' }}>
              {message.role === 'user' && <div className="ai-msg user">{message.text}</div>}
              {message.role === 'assistant' && message.text !== '' && (
                <div className={`ai-msg assistant ${message.isError ? 'is-error' : ''}`}>{message.text}</div>
              )}
              {message.toolCalls?.map((call, i) => (
                <div
                  key={`${index}-${i}`}
                  className={`ai-tool-chip ${call.ok ? '' : 'is-fail'}`}
                  title={call.summary}
                >
                  {call.ok ? 'OK' : 'ERR'} {call.name}
                </div>
              ))}
            </div>
          ))}

          {busy && <div className="ai-msg assistant">Working on the canvas…</div>}
        </div>

        <div className="ai-composer">
          <textarea
            value={input}
            placeholder={needsKey ? 'Add an API key in Settings first…' : 'Describe what the program should do…'}
            disabled={needsKey || busy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
          />
          <button onClick={() => void send(input)} disabled={needsKey || busy || input.trim() === ''} aria-label="Send message">
            <IconSend />
          </button>
        </div>
      </div>
    </aside>
  );
}
